import { forwardRef, Type } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import { IEffectiveErrorParams } from './model';

// Determines which error to display for a form control based on validation state and user interaction.
//
// Validation rules:
// 1. Never show errors on untouched controls (touched is set on blur or validateAll).
// 2. While the user is typing (typedWhileFocused):
//    - If the same custom error that was present when the field was focused is still present,
//      keep showing it (the user hasn't fixed it yet).
//    - Otherwise suppress all errors until blur. This covers:
//      a. New errors appearing while typing (e.g. typing an invalid email) — deferred to blur.
//      b. Errors that were fixed (e.g. required resolved by typing) — stay hidden.
//      c. Field cleared back to empty — required error deferred to blur.
// 3. After blur (typedWhileFocused is false), show errors normally:
//    - Custom error from [error] input takes priority.
//    - Then required error if the field is empty.
//
// The errorOnFocus field is managed by each component:
// - Set to the current custom error value on focus.
// - Cleared to null when the custom error resolves while focused (so re-introduction is suppressed).
// - Reset to null on blur.
export function getEffectiveError(params: IEffectiveErrorParams): string {
  if (!params.touched) {
    return '';
  }

  if (params.typedWhileFocused) {
    if (params.error && params.error === params.errorOnFocus) {
      return params.error;
    }

    return '';
  }

  if (params.error) {
    return params.error;
  }

  if (!params.isRequired) {
    return '';
  }

  const value = params.formControl.value;
  if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
    return params.isRequiredMessage;
  }

  return '';
}

export function setValueAccessor<T>(type: Type<T>) {
  return {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => type),
    multi: true,
  };
}
